import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { useGetAddressTypeList } from "../../../data/useGetAddressTypeList";
import { useDeleteAddressType } from "../../../data/useDeleteAddressType";
import { useEditAddressType } from "../../../data/useEditAddressType";
import { useCreateAddressType } from "../../../data/useCreateAddressType";
import { queryClient } from "../../../queryClient";
import editIcon from "../../../assets/settings/edit.svg";
import { Slider } from "../../../components/Slider";
import { Image } from "../../../components/Image";
import Select from "../../../components/Select";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import CustomTabs from "../../../components/CustomTabs";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
} from "react-icons/hi2";
import { Toggle } from "../../../components/Toggle";
import { useUpdateAddressTypeOrder } from "../../../data/useUpdateAddressTypeOrder";
import { Input } from "../../../components/Input";
import { BiGridVertical } from "react-icons/bi";
import { Modal } from "../../../components/Modal";
import { Form, Formik } from "formik";
import { addressTypeValidationSchema } from "../../../utils/validationSchema";

type Scope = "all" | "individual" | "entity";

type AddressTypeRow = {
  id: number;
  name: string;
  active: boolean;
  order: number | null;
  _type: "individual" | "entity";
  is_default: boolean;
  updated_at: string;
};

type DragState = { draggingId: number | null; overId: number | null };

type FormMode = "add" | "edit";

const COLS = "minmax(320px,1fr) 160px 120px 120px ";
const baseRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: COLS,
  alignItems: "center",
  columnGap: 24,
};
const headStyle: React.CSSProperties = {
  ...baseRow,
  padding: "10px 16px",
  color: theme.colors.muted,
};
const bodyRowStyle: React.CSSProperties = {
  ...baseRow,
  padding: "12px 16px",
  borderTop: "1px solid #F3F4F6",
};

const Row = ({
  item,
  onToggleActive,
  onEdit,
  draggableProps,
  isDragging,
  isToggling,
}: {
  item: AddressTypeRow;
  onToggleActive: (id: number, next: boolean) => void;
  onEdit: (item: AddressTypeRow) => void;
  draggableProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
  isToggling: boolean;
}) => {
  return (
    <div
      style={{
        ...bodyRowStyle,
        background: isDragging ? "#F9FAFB" : "transparent",
        cursor: "grab",
      }}
      {...draggableProps}
    >
      <Flex alignItems="center" gap="12">
        <BiGridVertical size={18} style={{ opacity: 0.7 }} />
        <Paragraph fontSize="2">{item.name}</Paragraph>
      </Flex>

      <Paragraph>
        {item._type === "individual" ? "Individual" : "Entity"}
      </Paragraph>

      <Paragraph color="gray.60">
        {item.is_default ? (
          "Default"
        ) : (
          <Image
            src={editIcon}
            alt="edit icon"
            height="24px"
            width="24px"
            style={{ cursor: "pointer" }}
            onClick={() => onEdit(item)}
          />
        )}
      </Paragraph>

      <Flex alignItems="center">
        {!item.is_default && (
          <Toggle
            isChecked={item.active}
            onChange={(e: any) => onToggleActive(item.id, e.target.checked)}
            mt={2}
            isDisabled={isToggling}
          />
        )}
      </Flex>
    </div>
  );
};

const SectionHeader = ({
  title,
  subtitle,
  onAdd,
}: {
  title: string;
  subtitle?: string;
  onAdd?: () => void;
}) => (
  <Flex justifyContent="space-between" alignItems="center">
    <Flex gap="8" alignItems="center">
      <Box>
        <H2 fontSize="3">{title}</H2>
        {subtitle ? (
          <Paragraph color="gray.60" fontSize="1">
            {subtitle}
          </Paragraph>
        ) : null}
      </Box>
    </Flex>
    {onAdd ? (
      <Button Icon={FaPlus} iconSize="16px" onClick={onAdd}>
        Add
      </Button>
    ) : null}
  </Flex>
);

const SettingsPropertiesAddressTypes = () => {
  const [activeTab, setActiveTab] = useState(0);
  const scope: Scope =
    activeTab === 0 ? "all" : activeTab === 1 ? "individual" : "entity";

  const { data, isLoading, isError } = useGetAddressTypeList(scope);

  const { mutate: deleteAddressType } = useDeleteAddressType();
  const { mutate: createAddressType } = useCreateAddressType();
  const { mutate: editAddressType } = useEditAddressType();
  const { mutate: updateAddressTypeOrder } = useUpdateAddressTypeOrder();

  const [isReordering, setIsReordering] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>("add");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formName, setFormName] = useState<string>("");
  const [formType, setFormType] = useState<"individual" | "entity">(
    "individual"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [doubleClickOnDelete, setDoubleClickOnDelete] = useState(false);

  const [list, setList] = useState<AddressTypeRow[]>([]);

  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleTabChange = (index: number) => setActiveTab(index);

  const typeOptions = [
    { label: "Individual", value: "individual" },
    { label: "Entity", value: "entity" },
  ];

  const openAdd = () => {
    setFormMode("add");
    setEditingId(null);
    setFormName("");
    setFormType(null);
    setIsFormOpen(true);
  };

  const openEdit = (item: AddressTypeRow) => {
    setFormMode("edit");
    setEditingId(item.id);
    setFormName(item.name ?? "");
    setFormType(item._type);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setIsFormOpen(false);
  };

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: ["address_type_list", scope] });
  };

  const onComplete = () => {
    invalidateLists();
    setIsFormOpen(false);
    setIsSubmitting(false);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
  };

  const onError = (err: any, fallbackMsg: string) => {
    setIsSubmitting(false);
    setIsDeleteModalOpen(false);
    setDoubleClickOnDelete(false);
    showError(err, fallbackMsg);
  };

  const onSuccessHandler = (message: string) => {
    notify(message, { type: "success", position: "bottom-left" });
    onComplete();
  };

  type AddressTypeFormValues = {
    name: string;
    type: "individual" | "entity";
  };

  const handleCreate = ({ name, type }: AddressTypeFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payload = { name: name.trim(), _type: type };

    createAddressType(payload as any, {
      onSuccess: () => onSuccessHandler("Address type added successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while creating the address type."),
    });
  };

  const handleUpdate = ({ name, type }: AddressTypeFormValues) => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      params: { name: name.trim(), _type: type },
      id: editingId,
    };

    editAddressType(payload as any, {
      onSuccess: () => onSuccessHandler("Address type edited successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while updating the address type."),
    });
  };

  const handleDelete = () => {
    if (!editingId || isSubmitting) return;
    setIsSubmitting(true);
    setDoubleClickOnDelete(true);
    deleteAddressType({ id: editingId } as any, {
      onSuccess: () => onSuccessHandler("Address type deleted successfully!"),
      onError: (err) =>
        onError(err, "A problem occurred while deleting the address type."),
    });
  };

  const items = useMemo<AddressTypeRow[]>(() => {
    const raw =
      (Array.isArray(data) && data) ||
      (Array.isArray((data as any)?.results) && (data as any).results) ||
      (Array.isArray((data as any)?.data) && (data as any).data) ||
      [];

    return (raw as any[]).map((r) => ({
      id: r.id,
      name: r.name,
      active: Boolean(r.active),
      order: r.order as number | null,
      _type: r._type === "entity" ? "entity" : "individual",
      is_default: Boolean(r.is_default),
      updated_at: r.updated_at ?? "",
    }));
  }, [data]);

  useEffect(() => {
    setList(items);
  }, [items]);

  const dragState = useRef<DragState>({ draggingId: null, overId: null });

  const reorderByIds = (
    arr: AddressTypeRow[],
    startId: number,
    endId: number
  ) => {
    const startIndex = arr.findIndex((x) => x.id === startId);
    const endIndex = arr.findIndex((x) => x.id === endId);
    if (startIndex === -1 || endIndex === -1 || startIndex === endIndex)
      return arr;
    const next = [...arr];
    const [moved] = next.splice(startIndex, 1);
    next.splice(endIndex, 0, moved);
    return next;
  };
  const handleDragStart = (id: number) => (e: React.DragEvent) => {
    dragState.current = { draggingId: id, overId: id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };

  const handleDragOver = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    dragState.current.overId = id;
  };

  const handleDragEnter = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    dragState.current.overId = id;
  };

  const handleDrop = (_scope: Scope) => (e: React.DragEvent) => {
    e.preventDefault();
    const { draggingId, overId } = dragState.current;
    if (!draggingId || !overId || draggingId === overId) return;

    const visibleIds = list.map((x) => x.id);
    if (!visibleIds.includes(draggingId) || !visibleIds.includes(overId))
      return;

    const reordered = reorderByIds(list, draggingId, overId);
    setList(reordered);

    setIsReordering(true);
    updateAddressTypeOrder(
      {
        orders: reordered.map((row, idx) => ({
          id: row.id,
          order: idx,
        })),
      },
      {
        onSuccess: () => {
          notify("Order updated!", {
            type: "success",
            position: "bottom-left",
          });
          setIsReordering(false);
          invalidateLists();
        },
        onError: (err) => {
          setIsReordering(false);
          showError(err, "Failed to update order.");
          invalidateLists();
        },
      }
    );

    dragState.current = { draggingId: null, overId: null };
  };

  const handleDragEnd = () => {
    dragState.current = { draggingId: null, overId: null };
  };

  const onToggleActive = (id: number, next: boolean) => {
    const target = list.find((r) => r.id === id);
    if (!target) return;

    setList((prev) =>
      prev.map((r) => (r.id === id ? { ...r, active: next } : r))
    );
    setTogglingId(id);

    const params = {
      name: target.name,
      _type: target._type,
      order: target.order ?? 0,
      active: next,
    };

    editAddressType(
      { id, params },
      {
        onSuccess: () => {
          notify(
            `Address type ${next ? "activated" : "deactivated"} successfully!`,
            { type: "success", position: "bottom-left" }
          );
          setTogglingId(null);
          invalidateLists();
        },
        onError: (err) => {
          setList((prev) =>
            prev.map((r) => (r.id === id ? { ...r, active: !next } : r))
          );
          setTogglingId(null);
          showError(
            err,
            `Failed to ${next ? "activate" : "deactivate"} the address type.`
          );
        },
      }
    );
  };

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <SectionHeader
        title="Address Types"
        subtitle="Configure for capturing previous income and employment history here."
        onAdd={openAdd}
      />

      {isLoading ? (
        <Paragraph color="gray.60" mt={3}>
          Loading…
        </Paragraph>
      ) : isError ? (
        <Paragraph color="red.60" mt={3}>
          Failed to load address types.
        </Paragraph>
      ) : (
        <>
          <CustomTabs
            variant="segmented"
            tabs={[
              {
                label: (
                  <>
                    <HiOutlineSquares2X2 size={16} />
                    All
                  </>
                ),
                content: null,
              },
              {
                label: (
                  <>
                    <HiOutlineUser size={16} />
                    Individual
                  </>
                ),
                content: null,
              },
              {
                label: (
                  <>
                    <HiOutlineBuildingOffice2 size={16} />
                    Entity
                  </>
                ),
                content: null,
              },
            ]}
            customMarginLeft="0"
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          <div style={headStyle}>
            <Paragraph>Name</Paragraph>
            <Paragraph>Type</Paragraph>
            <Paragraph></Paragraph>
            <Paragraph>Active</Paragraph>
          </div>

          {list.length === 0 ? (
            <Paragraph color="gray.60" px={4} py={4}>
              No address types found.
            </Paragraph>
          ) : (
            <div
              onDrop={handleDrop(scope)}
              onDragOver={(e) => e.preventDefault()}
              style={{
                borderTop: "1px solid #F3F4F6",
                opacity: isReordering ? 0.6 : 1,
                pointerEvents: isReordering ? "none" : "auto",
              }}
            >
              {list.map((item) => (
                <Row
                  key={item.id}
                  item={item}
                  onToggleActive={onToggleActive}
                  onEdit={openEdit}
                  isDragging={dragState.current.draggingId === item.id}
                  isToggling={togglingId === item.id}
                  draggableProps={{
                    draggable: true,
                    onDragStart: handleDragStart(item.id),
                    onDragOver: handleDragOver(item.id),
                    onDragEnter: handleDragEnter(item.id),
                    onDragEnd: handleDragEnd,
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <Slider isOpen={isFormOpen} onRequestClose={closeForm} width="600px">
        <Formik
          initialValues={{
            name: formName,
            type: formType,
          }}
          enableReinitialize
          validationSchema={addressTypeValidationSchema}
          validateOnMount
          onSubmit={(values) => {
            setFormName(values.name);
            setFormType(values.type);

            if (formMode === "add") {
              handleCreate(values as AddressTypeFormValues);
            } else {
              handleUpdate(values as AddressTypeFormValues);
            }
          }}
        >
          {(props) => (
            <Form>
              <Box px={4} py={3}>
                <H2 mb={4}>
                  {formMode === "add"
                    ? "Add Address Type"
                    : "Edit Address Type"}
                </H2>

                <Box mb={3}>
                  <Input
                    name="name"
                    label="Address Type Name"
                    value={props.values.name}
                    onChange={props.handleChange}
                    placeholder="Enter address type name"
                    isDisabled={isSubmitting}
                    hasError={Boolean(props.errors.name)}
                    errorMessage={props.errors.name as string}
                    isRequired
                  />
                </Box>

                <Box mb={5}>
                  <Select
                    label="Type"
                    name="type"
                    options={typeOptions}
                    value={typeOptions.find(
                      (o) => o.value === props.values.type
                    )}
                    onChange={(opt: any) =>
                      props.setFieldValue("type", opt?.value)
                    }
                    width="100%"
                    isRequired
                    isDisabled={isSubmitting}
                    hasError={Boolean(props.errors.type)}
                    errorMessage={props.errors.type as string}
                  />
                </Box>

                <Flex justifyContent="space-between" alignItems="center">
                  {formMode === "edit" ? (
                    <Button
                      variant="danger"
                      Icon={FaTrash}
                      iconSize="12px"
                      onClick={() => setIsDeleteModalOpen(true)}
                      isDisabled={isSubmitting}
                      iconColor="white"
                    >
                      Delete
                    </Button>
                  ) : (
                    <span />
                  )}

                  <Flex gap="20" alignItems="center">
                    <Button
                      variant="secondary"
                      onClick={closeForm}
                      isDisabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isDisabled={
                        !props.isValid || !props.dirty || isSubmitting
                      }
                    >
                      {isSubmitting ? "Saving…" : "Save"}
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            </Form>
          )}
        </Formik>
      </Slider>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete relationship type?"
        content="This will permanently remove the selected relationship type."
        onModalConfirmClick={() => {
          if (isSubmitting) return;

          handleDelete();
        }}
        secondaryButtonText="Cancel"
        primaryButtonText={isSubmitting ? "Deleting…" : "Delete"}
        primaryButtonVariant="danger"
        hasSecondaryButton
        width="450px"
        doubleClick={doubleClickOnDelete}
      />
    </Box>
  );
};

export default SettingsPropertiesAddressTypes;
